import { ref, onMounted, onBeforeUnmount } from 'vue';

export type HScrollDirection = 'left' | 'right';
const animationRunning = ref(false);

function isTextInput(el: Element | null): boolean {
  if (!el) return false;

  return (
    (el instanceof HTMLInputElement &&
      ['text', 'number', 'search', 'email', 'password', 'tel', 'url'].includes(el.type)) ||
    el instanceof HTMLTextAreaElement ||
    (el as HTMLElement).isContentEditable ||
    el.getAttribute('role') === 'textbox'
  );
}

export function useHDivScroll() {
  const scrollSpeed = ref(0);
  const scrollInterval = ref<ReturnType<typeof setInterval> | null>(null);
  const scrollEl = ref<HTMLElement | null>(null);
  const scrollStep = 1;
  const maxSpeed = 40;
  let currentDirection: HScrollDirection = 'right';

  function startScroll(direction: HScrollDirection) {
    if (!scrollEl.value || animationRunning.value) return;

    scrollEl.value.style.pointerEvents = 'none';

    animationRunning.value = true;
    currentDirection = direction;
    scrollSpeed.value = 0;

    if (scrollInterval.value) clearInterval(scrollInterval.value);

    scrollInterval.value = setInterval(() => {
      if (scrollSpeed.value < maxSpeed) {
        scrollSpeed.value += scrollStep;
      }

      scrollEl.value?.scrollBy({
        left: direction === 'left' ? -scrollSpeed.value : scrollSpeed.value,
        behavior: 'auto',
      });
    }, 16);
  }

  function stopScroll() {
    if (scrollInterval.value) {
      clearInterval(scrollInterval.value);
      scrollInterval.value = null;
    }

    scrollInterval.value = setInterval(() => {
      if (!scrollEl.value || scrollSpeed.value <= 0) {
        clearInterval(scrollInterval.value!);
        scrollInterval.value = null;
        scrollSpeed.value = 0;
        if (scrollEl.value) {
          scrollEl.value.style.pointerEvents = 'auto';
        }
        return;
      }

      scrollSpeed.value -= scrollStep;

      scrollEl.value.scrollBy({
        left: currentDirection === 'left' ? -scrollSpeed.value : scrollSpeed.value,
        behavior: 'auto',
      });
    }, 16);
    animationRunning.value = false;
  }

  function handleKey(e: KeyboardEvent) {
    if (isTextInput(document.activeElement)) return;

    if (e.key === 'ArrowLeft') {
      startScroll('left');
    } else if (e.key === 'ArrowRight') {
      startScroll('right');
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKey);
    window.addEventListener('keyup', stopScroll);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKey);
    window.removeEventListener('keyup', stopScroll);
  });

  return {
    startScroll,
    stopScroll,
    scrollEl,
  };
}
